'use client';

import { cn } from '@ui/lib/utils';
import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Input } from './input';

export interface PasswordStrengthResult {
  score: number;
  feedback: {
    warning?: string;
    suggestions: string[];
  };
}

// This is a simplified zxcvbn implementation
// In a production environment, you would use the actual zxcvbn library
const calculatePasswordStrength = (
  password: string
): PasswordStrengthResult => {
  if (!password) {
    return {
      score: 0,
      feedback: {
        warning: '',
        suggestions: [],
      },
    };
  }

  // Very basic strength calculation
  let score = 0;
  const suggestions: string[] = [];

  // Check for character types
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  // Length check
  if (password.length > 7) score += 1;
  if (password.length > 10) score += 1;

  // Has uppercase, lowercase, numbers, and special characters
  if (hasUppercase) score += 0.5;
  else suggestions.push('Add uppercase letters');

  if (hasLowercase) score += 0.5;
  else suggestions.push('Add lowercase letters');

  if (hasNumbers) score += 0.5;
  else suggestions.push('Add numbers');

  if (hasSpecial) score += 0.5;
  else suggestions.push('Add special characters');

  // Check for variety
  const hasVariety =
    (password.length > 0 && hasUppercase && hasLowercase) ||
    ((hasUppercase || hasLowercase) && hasNumbers) ||
    ((hasUppercase || hasLowercase || hasNumbers) && hasSpecial);

  if (hasVariety) score += 1;

  // Cap at 4 by default
  let cappedScore = Math.min(Math.floor(score), 4);

  // Enforce requirement for "Very Strong" - must have all character types
  if (
    cappedScore === 4 &&
    !(hasUppercase && hasLowercase && hasNumbers && hasSpecial)
  ) {
    cappedScore = 3; // Downgrade to "Strong" if missing any character type

    // Add specific suggestions for reaching "Very Strong"
    if (!hasUppercase)
      suggestions.push('Add uppercase letters for higher strength');
    if (!hasLowercase)
      suggestions.push('Add lowercase letters for higher strength');
    if (!hasNumbers) suggestions.push('Add numbers for higher strength');
    if (!hasSpecial)
      suggestions.push('Add special characters for higher strength');
  }

  // Generate warning based on score
  let warning = '';
  if (cappedScore < 2) {
    warning = 'This password is too weak';
  }

  return {
    score: cappedScore,
    feedback: {
      warning,
      suggestions,
    },
  };
};

export interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  showStrengthMeter?: boolean;
  strengthColors?: string[];
  strengthLabels?: string[];
  showStrengthGuide?: boolean;
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      className,
      showStrengthMeter = true,
      strengthColors = [
        'bg-red-500',
        'bg-orange-400',
        'bg-yellow-400',
        'bg-green-400',
        'bg-green-600',
      ],
      strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'],
      showStrengthGuide = true,
      onChange,
      ...props
    },
    ref
  ) => {
    const [visible, setVisible] = useState(false);
    const [value, setValue] = useState('');
    const [strength, setStrength] = useState<PasswordStrengthResult>({
      score: 0,
      feedback: {
        warning: '',
        suggestions: [],
      },
    });

    const strengthGuideText = [
      'Try a longer password with mixed characters',
      'Add more characters and mix character types',
      'Good start! Mix in numbers and special characters',
      'Almost there! Ensure you have uppercase, lowercase, numbers, and special characters',
      'Excellent! Your password has good length and character variety',
    ];

    const toggleVisibility = useCallback(() => {
      setVisible((prev) => !prev);
    }, []);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);

        // Call the parent's onChange handler if provided
        if (onChange) {
          onChange(e);
        }
      },
      [onChange]
    );

    // Calculate password strength when value changes
    useEffect(() => {
      setStrength(calculatePasswordStrength(value));
    }, [value]);

    return (
      <div className="space-y-2 mt-2">
        <div className="relative">
          <Input
            type={visible ? 'text' : 'password'}
            className={cn('pr-10', className)}
            onChange={handleChange}
            ref={ref}
            {...props}
          />
          <button
            type="button"
            onClick={toggleVisibility}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-700 focus:outline-none"
            tabIndex={-1}
            aria-label={visible ? 'Hide password' : 'Show password'}
          >
            {visible ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>

        {showStrengthMeter && value && (
          <div className="space-y-1 px-0.5">
            <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-300',
                  strengthColors[strength.score]
                )}
                style={{ width: `${(strength.score + 1) * 20}%` }}
              />
            </div>
            <div className="flex justify-between text-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-slate-700 font-bold">
                  {strengthLabels[strength.score]}
                </span>
                <div
                  className={cn(
                    'h-2 w-2 rounded-full inline-block animate-blink',
                    strengthColors[strength.score]
                  )}
                  aria-hidden="true"
                />
                {showStrengthGuide && (
                  <span className="text-slate-600">
                    {strengthGuideText[strength.score]}
                  </span>
                )}
              </div>
              {strength.feedback.warning && (
                <span className="text-red-500 ml-2 shrink-0">
                  {strength.feedback.warning}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
